import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, Typography, Box, Skeleton, Alert, Button, Stack, Chip } from '@mui/material';
import { useChart } from '../../chart';
import Iconify from '../../iconify/Iconify';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#9c27b0', '#0288d1'];
const MAX_LEGEND_ITEMS = 6;

const getTodaySnapshot = (recentActivities) => {
  const today = new Date();
  const isSameDate = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const countByStatus = {};
  (recentActivities || []).forEach((activity) => {
    if (!activity?.status || !activity?.at) return;
    const atDate = new Date(activity.at);
    if (Number.isNaN(atDate.getTime())) return;
    if (!isSameDate(today, atDate)) return;
    countByStatus[activity.status] = (countByStatus[activity.status] || 0) + 1;
  });

  return Object.entries(countByStatus)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);
};

export default function StatusDonut({ data, loading, error, onRetry, recentActivities }) {
  const normalized = (data || []).map((item) => ({
    status: item.status || 'Unknown',
    count: Number(item.count || 0),
  }));

  const todaySnapshot = getTodaySnapshot(recentActivities);
  const total = normalized.reduce((sum, item) => sum + item.count, 0);
  const chartRows = normalized.slice(0, MAX_LEGEND_ITEMS);
  const series = chartRows.map((item) => item.count);
  const labels = chartRows.map((item) => item.status);

  const chartOptions = useChart({
    labels,
    colors: COLORS,
    legend: { show: false },
    plotOptions: { pie: { donut: { size: '70%' } } },
    dataLabels: { enabled: false },
    stroke: { width: 2, colors: ['#fff'] },
    tooltip: {
      y: {
        formatter: (value) => Number(value || 0).toLocaleString(),
      },
    },
  });

  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 22px rgba(16, 24, 40, 0.05)',
        height: '100%',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Iconify icon="solar:pie-chart-2-bold-duotone" width={18} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Status Breakdown
        </Typography>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1, fontSize: { xs: '0.72rem', md: '0.75rem' } }}
      >
        Distribution by current package status
      </Typography>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Failed to load status breakdown.
        </Alert>
      ) : loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      ) : (
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1.5}>
            <Box sx={{ width: '52%' }} dir="ltr">
              <ReactApexChart type="donut" height={220} series={series} options={chartOptions} />
            </Box>

            <Stack spacing={1} sx={{ width: '48%', pr: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Total: {total.toLocaleString()}
              </Typography>

              {chartRows.map((item, index) => {
                const pct = total > 0 ? ((item.count * 100) / total).toFixed(1) : '0.0';
                return (
                  <Stack
                    key={`${item.status}-${index}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 0.35 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: COLORS[index % COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="caption" noWrap title={item.status}>
                        {item.status}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {item.count.toLocaleString()} ({pct}%)
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>

          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
              Today Snapshot
            </Typography>

            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              {todaySnapshot.length > 0 ? (
                todaySnapshot.map((item) => (
                  <Chip key={item.status} size="small" variant="outlined" label={`${item.status}: ${item.count}`} />
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No status activity for today.
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      )}
    </Card>
  );
}
